import { isDailyLimit, noteDailyLimit } from "../Common/GateLimit";
import Place, { Place_Source } from "./Place";
import Common, { API_BASE_URL, PLACES_PROVIDER_ORDER, AICategories, AIBaseCategories } from "../Common/Common";
import HasDataPlaces from "./HasDataPlaces";
import OpenWebNinjaPlaces from "./OpenWebNinjaPlaces";
import { getFeaturedCityBucket } from "../Common/FeaturedPlaceImages";
import { googlePhotoFields } from "../Common/googlePhoto";

// Google's raw `types` (machine names like "tourist_attraction",
// "art_gallery") aren't real guide category IDs, so Guide.categoryNamesFromIDs
// looks each one up, finds nothing, and shows "NA". Maps to the guide's own
// AICategories/AIBaseCategories taxonomy instead, same fuzzy-match
// convention AiSearch.js already uses for AI-sourced places. Returns null
// (leaving the place uncategorized) rather than guessing — id "53" is a real
// category literally named "Search" with its own icon, so falling back to it
// for an unmatched place silently mislabeled it as if the user had searched
// for "Search" specifically. Ported from andiamo-places' identical helper
// (originally BuildMyTripWizard.js's NewGuideWizard.js port), 2026-08-31.
export const mapGoogleTypesToCategoryId = (types, aiCategories, aiBaseCategories) => {
  const labels = (types || []).map((t) => t.replace(/_/g, " "));
  for (const cat of aiCategories || []) {
    if (labels.some((label) => Common.tokenSubstringMatch(cat.category, label))) return cat.id;
  }
  for (const cat of aiBaseCategories || []) {
    if (labels.some((label) => Common.tokenSubstringMatch(cat.category, label))) return cat.id;
  }
  // Every caller (TripAI, AISearch, BuildMyTripWizard, PlacesHeader's "Fix
  // Categories") funnels through here, so logging the actual raw types that
  // failed to match — rather than each caller separately logging its own
  // higher-level guess text — is what's needed to find and add the missing
  // taxonomy synonym (see the Safari/Game Reserve fix in Common.js for the
  // pattern this feeds). Only fires when types were actually provided; a
  // place from a source that never passed real types wouldn't be a
  // taxonomy gap. Added 2026-09-24.
  if ((types || []).length > 0) {
    console.log("mapGoogleTypesToCategoryId: no category match for types:", types);
  }
  return null;
};

export default class GooglePlaces {
  constructor() {
    this.places = [];
  }

  //&input=Museum%20of%20Contemporary%20Art%20Australia
  //&keyword=
  url = `https://maps.googleapis.com/maps/api/place/findplacefromtext/json?
 locationbias=rectangle:48.80,2.27|48.88,2.42
 &inputtype=textquery
 &type=museum
 &fields=photos,formatted_address,name,rating,opening_hours,geometry,icon,place_id,opening_hours
 &rankby
 &key=${process.env.REACT_APP_GOOGLE_PLACES_KEY}`;
  //https://maps.googleapis.com/maps/api/place/findplacefromtext/json?input=Louvre&inputtype=textquery&fields=photos,formatted_address,name,opening_hours,rating&locationbias=rectangle:48.80,2,27|48.88,2.42&key=${process.env.REACT_APP_GOOGLE_PLACES_KEY}
  //https://maps.googleapis.com/maps/api/place/textsearch/json?query=Louvre&location=48.3,2.3&radius=15000&type=museum&key=${process.env.REACT_APP_GOOGLE_PLACES_KEY}

  next_page_token = null;
  AllPlaces = [];
  maxSearches = 3;
  url = "";
  callback = null;

  searchPlaces = (location, radius, type, query, callback, label) => {
    //const callback = this.getDetails;
    this.next_page_token = null;
    this.AllPlaces = [];
    this.maxSearches = 3;
    this.callback = callback;
    var params = {
      location: location,
      radius: radius, //meters up to 50,000
      //format: "json",
      key: process.env.REACT_APP_GOOGLE_PLACES_KEY,
    };
    if (query != null && query !== "") params.query = query.replace(/ /g, "%20");
    if (type != null) params.type = type;

    //console.log(params);
    var url = `${API_BASE_URL}/google_text_search.php?`;
    if (type != null) url = `${API_BASE_URL}/google_category_search.php?`;
    //var url = `https://maps.googleapis.com/maps/api/place/textsearch/json?`;
    Object.keys(params).forEach(function (key) {
      url += "&" + key + "=" + params[key];
    });
    this.url = url;
    this.executeSearch(url, this.executeSearchCallback, label);
  };

  executeSearchCallback = (results, next_page_token, label) => {
    if (results == null || !Array.isArray(results)) {
      this.callback(this.AllPlaces, label);
      return;
    }
    this.maxSearches--;
    //console.log("this.maxSearches", this.maxSearches);
    if (Array.isArray(this.AllPlaces)) this.AllPlaces.push(...results);
    //console.log("executeSearchCallback", this.AllPlaces);
    //console.log("next_page_token", next_page_token);
    if (this.maxSearches <= 0 || next_page_token == null || typeof next_page_token === "undefined") {
      this.callback(this.AllPlaces, label);
      return;
    }
    let page_url = this.url + "&pagetoken=" + next_page_token;
    //console.log("page_url", page_url);
    setTimeout(() => this.executeSearch(page_url, this.executeSearchCallback, label), 500);
  };

  executeSearch = (url, callback, label) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    fetch(url, { signal: controller.signal })
      .then(function (response) {
        clearTimeout(timeoutId);
        return response.json();
      })
      .then(function (response) {
        callback(response.results, response.next_page_token, label);
      })
      .catch(function (error) {
        clearTimeout(timeoutId);
        console.log(error);
        callback(null, null, label);
      });
  };

  _last = 0;
  _index = 0;
  _places = [];
  _results = [];
  _callback = null;

  // aiCategories/aiBaseCategories (added 2026-09-24) — optional, default to
  // [] so every existing 3-arg caller (Map.js, WalkAI.js) is unaffected.
  // Threaded through to parsePlace so it can resolve a real Category from
  // google_place.types instead of always leaving it empty — see parsePlace's
  // own comment for the full story (this was a real, pre-existing gap
  // shared by every feature that calls getQuickDetails: TripAI, AISearch,
  // WalkAI, Map — none of them ever got a resolved category out of this
  // path, unlike searchCityAttractions's callers). TripAICountry.js was
  // also a caller when this comment was first written, but turned out to
  // be dead code (never imported anywhere) and was deleted 2026-09-24.
  getQuickDetails = (gplaces, callback, label, places, aiCategories = [], aiBaseCategories = []) => {
    // Thread the growing new_places array through as parsePlace's `places` arg
    // (matching getDetails/_results below) so each result's ID is checked for
    // uniqueness against the others already produced this batch — the caller's
    // `places` arg is otherwise unused/undefined here, which previously meant
    // every result's ID stayed at the Place constructor default ("") since
    // neither of parsePlace's ID-assignment branches ever ran.
    //
    // This is called once per attraction (not once for the whole search),
    // each call starting a fresh, empty new_places — so that alone only
    // caught collisions between results of the SAME attraction's Google
    // lookup, never between different attractions' preview pins piling up
    // across the run. That's exactly what the caller's `places` argument is
    // for — merge it in for the uniqueness check too, without adding its
    // entries to the new_places actually returned. Same fix as mobile's
    // GooglePlaces.js.
    let new_places = [];
    const existingPlaces = Array.isArray(places) ? places : [];
    gplaces.forEach((place) => {
      let new_place = this.parsePlace(place, {}, null, true, label, [...existingPlaces, ...new_places], aiCategories, aiBaseCategories);
      new_places.push(new_place);
    });
    callback(new_places, label);
  };

  getDetails = (places, callback) => {
    this._places = places;
    this._last = places.length - 1;
    this._callback = callback;
    this._index = 0;
    this._results = [];
    if (places.length == 0) { callback(this._results); return; }

    //console.log("places:", places.length, places);
    this.getOneDetails(this.getDetailsCallback);
    // for (let i = 0; i < places.length; i++) {
    //   setTimeout(() => {
    //     this.getOneDetails(_places, places, callback, i, this._last, getDetailsCallback);
    //   }, i * 200);
    // }
  };

  getDetailsCallback = (place, details) => {
    //console.log(details);
    if (details != null) {
      let new_place = this.parsePlace(place, details, this._results);
      this._results.push(new_place);
    }
    this._index += 1;
    if (this._places[this._index] == null || this._places[this._index] == "") this._index += 1;
    if (this._index >= this._last) {
      this._callback(this._results);
      return;
    }
    setTimeout(() => {
      this.getOneDetails(this.getDetailsCallback);
    }, 100);
  };
  //&fields=rating,formatted_phone_number,address_component,adr_address,business_status,icon,formatted_address,photo,type,url,price_level,website
  //  , geometry, , permanently_closed (deprecated), photo, place_id, plus_code, type, url, utc_offset, vicinity
  //https://maps.googleapis.com/maps/api/place/details/json?place_id=ChIJgRpG1DLx5UcRxN9rC9PF4KE&fields=rating,formatted_phone_number,address_component,adr_address,business_status,icon,formatted_address,photo,type,url,price_level,website&key=${process.env.REACT_APP_GOOGLE_PLACES_KEY}
  getOneDetails = (callback, place) => {
    //console.log("getOneDetails", place);
    if (place == null) place = this._places[this._index];
    if (typeof place == "undefined") callback(place, null);
    //console.log("place2", place);
    if (place == null) callback(place, null);
    if (place == "") callback(place, null);
    if (typeof place == "undefined") callback(place, null);
    if (typeof place.place_id == "undefined") callback(place, null);
    const url = `${API_BASE_URL}/google_get_details.php?place_id=${place.place_id}`;
    //const url = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${place.place_id}&fields=name,rating,formatted_phone_number,address_component,adr_address,business_status,icon,formatted_address,photo,type,url,price_level,website&key=${process.env.REACT_APP_GOOGLE_PLACES_KEY}`;
    //console.log("getDetails", url);
    fetch(url)
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        //("response", JSON.stringify(response));
        callback(place, response.result);
      })
      .catch(function (error) {
        console.log(error);
        callback(place, null);
      });
  };

  static getMoreDetails = (callback, google_place_id) => {
    //console.log("place1", place);
    const url = `${API_BASE_URL}/google_get_details.php?place_id=${google_place_id}`;
    fetch(url)
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        //console.log("response", JSON.stringify(response));
        callback(google_place_id, response.result);
      })
      .catch(function (error) {
        //console.log(error);
        callback(google_place_id, null);
      });
  };

  // aiCategories/aiBaseCategories (added 2026-09-24, default []) — every
  // pre-existing caller (getDetailsCallback below, OpenTripMap.js/
  // Attractions.js's own separate parsePlace methods are unrelated classes)
  // omits these, so behavior is unchanged for them: mapGoogleTypesToCategoryId
  // against two empty arrays always returns null, same as the unconditional
  // `[]` this replaced. Only getQuickDetails' callers (TripAI, AISearch)
  // that now pass real categories get an actual resolved Category.
  parsePlace = (google_place, details, results, getQuickDetails, label, places, aiCategories = [], aiBaseCategories = []) => {
    //console.log("details", details);
    //console.log("details", JSON.stringify(details));
    let new_place = null;
    if (typeof places === "undefined" || places === null) new_place = new Place();
    else new_place = new Place();
    // new Place() above already assigned a proper ID checked against the real
    // guide (window.guide_obj.places, since window.web_app is true here) — only
    // fall back to checking against this search batch if that didn't happen.
    if (!new_place.ID) {
      if (results != null) new_place.ID = new_place.createIDFromPlacesArray(results);
      else if (places != null) new_place.ID = new_place.createIDFromPlacesArray(places);
    }
    if (typeof google_place.name != "undefined") new_place.Name = google_place.name;
    if (typeof details.name != "undefined") new_place.Name = details.name;
    //if (typeof label != "undefined") new_place.Name = label;
    if (typeof google_place.geometry.location == "undefined") console.log("details.point.lat", details);
    else {
      new_place.Lat = parseFloat(google_place.geometry.location.lat);
      new_place.Longi = parseFloat(google_place.geometry.location.lng);
    }
    if (typeof google_place.formatted_address != "undefined") new_place.Address = google_place.formatted_address;

    // if (typeof details.address.city != "undefined") new_place.City = details.address.city;
    // if (typeof details.address.state != "undefined") new_place.State = details.address.state;
    // if (typeof details.address.country != "undefined") new_place.Country = details.address.country;
    if (typeof details.international_phone_number != "undefined") new_place.Phone = details.international_phone_number;

    new_place.Email = "";
    if (typeof details.rating != "undefined") new_place.Rating = details.rating;

    // Confirmed live 2026-09-24: Google's legacy Text Search response
    // (google_text_search.php/google_category_search.php, what feeds
    // getQuickDetails via searchPlaces) includes a `types` array per result
    // by default — no special fields request needed, unlike Find Place.
    // This was previously discarded entirely (always `[]`), the real
    // gap behind "category not found" reports from every getQuickDetails
    // caller (TripAI, AISearch, WalkAI, Map) — none of them ever got a
    // resolved category out of this path, unlike
    // searchCityAttractions's callers (Build My Trip) which already used
    // mapGoogleTypesToCategoryId on real type data. `details.types` is
    // preferred when present (the full Place Details response, via
    // getDetailsCallback below, can carry it too) since it's the more
    // complete of the two when both exist.
    const rawTypes = typeof details.types !== "undefined" ? details.types
      : (typeof google_place.types !== "undefined" ? google_place.types : undefined);
    new_place.Category = typeof rawTypes !== "undefined"
      ? [mapGoogleTypesToCategoryId(rawTypes, aiCategories, aiBaseCategories)].filter(Boolean)
      : [];
    if (typeof details.url != "undefined") {
      if (details.url.includes(";")) {
        const urls = details.url.split(";");
        new_place.Hlink = `google|${urls[0]}`;
      } else new_place.Hlink = `google|${details.url}`;
    }
    //if (typeof details.address.suburb != "undefined") new_place.Group = details.address.suburb;
    new_place.Group = [];
    // if (typeof details.icon != "undefined") new_place.Photo = details.icon;
    // if (typeof details.icon != "undefined") new_place.Photo_m = details.icon;
    new_place.Photo = "https://andiamotravelapp.com/Images/App/IllustratedImage.jpg"; //"http://liquidguides.com/Media/Images/google-places-logo.jpg";
    new_place.Photo_m = "https://andiamotravelapp.com/Images/App/IllustratedImage.jpg"; //"http://liquidguides.com/Media/Images/google-places-logo.jpg";
    new_place.Info = "";
    if (typeof details.opening_hours != "undefined") {
      const parsedHours = GooglePlaces.mapLegacyOpeningHours(details.opening_hours);
      if (parsedHours) new_place.OpeningHours = parsedHours;
    }
    //if (typeof details.url != "undefined") new_place.URL = details.url;
    new_place.PType = "Icon";
    new_place.Points = null;
    new_place.Source = Place_Source.GOOGLEPLACES;

    if (results === null) {
      if (typeof google_place.rating != "undefined") new_place.Rating = google_place.rating;
      if (typeof google_place.place_id != "undefined") {
        if (getQuickDetails) new_place.Hlink = `google|https://maps.google.com/?place_id=${google_place.place_id}`;
        else new_place.Hlink = `google|https://maps.google.com/?cid=${google_place.place_id}`;
      }
    }
    //console.log(new_place);
    return new_place;
  };

  parsePOI = (poi, details, results, aiCategories = [], aiBaseCategories = []) => {
    //console.log("details", details);
    //console.log("details", JSON.stringify(details));
    let new_place = new Place();
    new_place.createIDFromPlacesArray(results);
    if (typeof poi.name != "undefined") new_place.Name = poi.name;
    if (typeof details.name != "undefined") new_place.Name = details.name;
    if (typeof poi.coordinate != "undefined") {
      if (typeof poi.coordinate.latitude != "undefined") new_place.Lat = parseFloat(poi.coordinate.latitude);
      if (typeof poi.coordinate.longitude != "undefined") new_place.Longi = parseFloat(poi.coordinate.longitude);
    } else {
      new_place.Lat = poi.latLng.lat();
      new_place.Longi = poi.latLng.lng();
    }
    if (typeof poi.formatted_address != "undefined") new_place.Address = poi.formatted_address;

    // if (typeof details.address.city != "undefined") new_place.City = details.address.city;
    // if (typeof details.address.state != "undefined") new_place.State = details.address.state;
    // if (typeof details.address.country != "undefined") new_place.Country = details.address.country;
    if (typeof details.international_phone_number != "undefined") new_place.Phone = details.international_phone_number;

    new_place.Email = "";
    if (typeof details.rating != "undefined") new_place.Rating = details.rating;
    new_place.Category = typeof details.types != "undefined"
      ? [mapGoogleTypesToCategoryId(details.types, aiCategories, aiBaseCategories)].filter(Boolean)
      : [];
    if (typeof details.url != "undefined") {
      if (details.url.includes(";")) {
        const urls = details.url.split(";");
        new_place.Hlink = `google|${urls[0]}`;
      } else new_place.Hlink = `google|${details.url}`;
    }

    //if (typeof details.address.suburb != "undefined") new_place.Group = details.address.suburb;
    new_place.Group = [];
    //if (typeof details.icon != "undefined") new_place.Photo = details.icon;
    //if (typeof details.icon != "undefined") new_place.Photo_m = details.icon;
    new_place.Photo = "https://andiamotravelapp.com/Images/App/IllustratedImage.jpg"; //"http://liquidguides.com/Media/Images/google-places-logo.jpg";
    new_place.Photo_m = "https://andiamotravelapp.com/Images/App/IllustratedImage.jpg" //"http://liquidguides.com/Media/Images/google-places-logo.jpg";
    new_place.Info = "";
    //if (typeof details.url != "undefined") new_place.URL = details.url;
    new_place.PType = "Icon";
    new_place.Points = null;
    new_place.Source = Place_Source.GOOGLEPLACES;
    //console.log(new_place);
    return new_place;
  };

  static parsePOIStatic = (poi, details, results, aiCategories = [], aiBaseCategories = []) => {
    //console.log("details", details);
    //console.log("details", JSON.stringify(details));
    let new_place = new Place();
    new_place.createIDFromPlacesArray(results);
    if (typeof poi.name != "undefined") new_place.Name = poi.name;
    if (typeof details.name != "undefined") new_place.Name = details.name;
    if (typeof poi.coordinate != "undefined") {
      if (typeof poi.coordinate.latitude != "undefined") new_place.Lat = parseFloat(poi.coordinate.latitude);
      if (typeof poi.coordinate.longitude != "undefined") new_place.Longi = parseFloat(poi.coordinate.longitude);
    } else {
      new_place.Lat = poi.latLng.lat();
      new_place.Longi = poi.latLng.lng();
    }
    if (typeof poi.formatted_address != "undefined") new_place.Address = poi.formatted_address;

    // if (typeof details.address.city != "undefined") new_place.City = details.address.city;
    // if (typeof details.address.state != "undefined") new_place.State = details.address.state;
    // if (typeof details.address.country != "undefined") new_place.Country = details.address.country;
    if (typeof details.international_phone_number != "undefined") new_place.Phone = details.international_phone_number;

    new_place.Email = "";
    if (typeof details.rating != "undefined") new_place.Rating = details.rating;
    new_place.Category = typeof details.types != "undefined"
      ? [mapGoogleTypesToCategoryId(details.types, aiCategories, aiBaseCategories)].filter(Boolean)
      : [];
    if (typeof details.url != "undefined") {
      if (details.url.includes(";")) {
        const urls = details.url.split(";");
        new_place.Hlink = `google|${urls[0]}`;
      } else new_place.Hlink = `google|${details.url}`;
    }

    //if (typeof details.address.suburb != "undefined") new_place.Group = details.address.suburb;
    new_place.Group = [];
    //if (typeof details.icon != "undefined") new_place.Photo = details.icon;
    //if (typeof details.icon != "undefined") new_place.Photo_m = details.icon;
    new_place.Photo = "https://andiamotravelapp.com/Images/App/IllustratedImage.jpg"; //"http://liquidguides.com/Media/Images/google-places-logo.jpg";
    new_place.Photo_m = "https://andiamotravelapp.com/Images/App/IllustratedImage.jpg"; //"http://liquidguides.com/Media/Images/google-places-logo.jpg";
    new_place.Info = "";
    //if (typeof details.url != "undefined") new_place.URL = details.url;
    new_place.PType = "Icon";
    new_place.Points = null;
    new_place.Source = Place_Source.GOOGLEPLACES;
    //console.log(new_place);
    return new_place;
  };

  moveToGuide = (guide, place) => {
    guide.places.addPlace(place);
  };

  static getGoogleCategories = () => {
    var google_categories = [
      "accounting",
      "airport",
      "amusement_park",
      "aquarium",
      "art_gallery",
      "atm",
      "bakery",
      "bank",
      "bar",
      "beauty_salon",
      "bicycle_store",
      "book_store",
      "bowling_alley",
      "bus_station",
      "cafe",
      "campground",
      "car_dealer",
      "car_rental",
      "car_repair",
      "car_wash",
      "casino",
      "cemetery",
      "church",
      "city_hall",
      "clothing_store",
      "convenience_store",
      "courthouse",
      "dentist",
      "department_store",
      "doctor",
      "drugstore",
      "electrician",
      "electronics_store",
      "embassy",
      "fire_station",
      "florist",
      "funeral_home",
      "furniture_store",
      "gas_station",
      "gym",
      "hair_care",
      "hardware_store",
      "hindu_temple",
      "home_goods_store",
      "hospital",
      "insurance_agency",
      "jewelry_store",
      "laundry",
      "lawyer",
      "library",
      "light_rail_station",
      "liquor_store",
      "local_government_office",
      "locksmith",
      "lodging",
      "meal_delivery",
      "meal_takeaway",
      "mosque",
      "movie_rental",
      "movie_theater",
      "moving_company",
      "museum",
      "night_club",
      "painter",
      "park",
      "parking",
      "pet_store",
      "pharmacy",
      "physiotherapist",
      "plumber",
      "police",
      "post_office",
      "primary_school",
      "real_estate_agency",
      "restaurant",
      "roofing_contractor",
      "rv_park",
      "school",
      "secondary_school",
      "shoe_store",
      "shopping_mall",
      "spa",
      "stadium",
      "storage",
      "store",
      "subway_station",
      "supermarket",
      "synagogue",
      "taxi_stand",
      "tourist_attraction",
      "train_station",
      "transit_station",
      "travel_agency",
      "university",
      "veterinary_care",
      "zoo",
    ];
    let googleCategories = [];
    google_categories.forEach((cat) => {
      googleCategories.push({
        label: cat,
        value: cat,
      });
    });
    return googleCategories;
  };

  // ── Places API (New) ──────────────────────────────────────────────────────

  // wantHours=true (only fetchOpeningHours sets it) asks the endpoint for
  // rating/websiteUri/priceLevel/regularOpeningHours too — see
  // google_places_new_search.php's comment for why this is a cost split,
  // not a feature flag: the default (false) search path stays at Pro-tier
  // pricing, while the hours path is already paying Enterprise for
  // regularOpeningHours regardless, so including the other three costs
  // nothing extra there.
  // PLACES_PROVIDER (Common.js, REACT_APP_PLACES_PROVIDER at build time)
  // picks which backend actually answers this — "google" (official, billed
  // Places API New), "hasdata", or "openwebninja" (both unofficial
  // scraper-backed alternatives, see project memory:
  // project_paid_api_cost_mitigation_analysis). Every caller (this file's
  // own searchCityAttractions/fetchOpeningHours, and every other caller of
  // this method across the app) is unaffected by which provider answers —
  // they all get back the same Place[] shape either way. Added 2026-09-24.
  // Ordered chain over PLACES_PROVIDER_ORDER (Common.js): tries each
  // provider in turn, and only moves to the next one on a genuine upstream
  // failure (network error, or an explicit `error` field — see
  // hasdata_places_search.php's/openwebninja_places_search.php's own
  // comments on why httpCode>=400 is distinguished from a legitimate
  // zero-result search server-side) — never on a real "nothing found"
  // search, which would double the cost of every no-match query for no
  // benefit. This is the same fallback contract the old hardcoded
  // "hasdata"/"openwebninja"-else-Google branch had, just generalized to an
  // arbitrary ordered list instead of a fixed 2-tier fallback — reordering
  // or adding a provider (e.g. a future Wikipedia-backed entry) is then a
  // PLACES_PROVIDER_ORDER config change, not a code change. Exactly the
  // outage scenario that hit HasData's exhausted credits 2026-09-24 — the
  // chain keeps every place search in the app from silently going empty.
  // aiCategories/aiBaseCategories (added 2026-09-24, both default []) —
  // optional, threaded through to parseNewAPIPlace/parseHasDataPlace/
  // parseOpenWebNinjaPlace so they can resolve a REAL guide category ID the
  // same way parsePlace (the legacy search path) already does, instead of
  // leaving Category as Google's raw, unmapped type strings. Every existing
  // caller that doesn't pass these (searchCityAttractions/
  // BuildMyTripWizard.js, KMLInput.js, fetchOpeningHours, etc.) is
  // completely unaffected — see parseNewAPIPlace's own comment for why.
  searchPlacesNewAPI = (textQuery, includedType, lat, lng, radiusMeters, callback, wantHours = false, aiCategories = [], aiBaseCategories = []) => {
    const runProvider = (index) => {
      if (index >= PLACES_PROVIDER_ORDER.length) {
        // Chain exhausted with no entry actually answering (shouldn't
        // normally happen since "google" — the guaranteed, always-available
        // entry — is expected in the list) — final safety net.
        this._searchPlacesGoogle(textQuery, includedType, lat, lng, radiusMeters, callback, wantHours, aiCategories, aiBaseCategories);
        return;
      }
      const provider = PLACES_PROVIDER_ORDER[index];
      const tryNext = () => runProvider(index + 1);
      if (provider === "hasdata") {
        this._searchPlacesHasData(textQuery, lat, lng, callback, tryNext, aiCategories, aiBaseCategories);
      } else if (provider === "openwebninja") {
        this._searchPlacesOpenWebNinja(textQuery, lat, lng, callback, tryNext, aiCategories, aiBaseCategories);
      } else {
        // "google", or any unrecognized entry — Google is always the
        // terminal call (no onFailure/next step; it's the authoritative
        // source and has nothing further to fall back to).
        this._searchPlacesGoogle(textQuery, includedType, lat, lng, radiusMeters, callback, wantHours, aiCategories, aiBaseCategories);
      }
    };
    runProvider(0);
  };

  // Same ordered chain as searchPlacesNewAPI, for a single photo lookup —
  // used by every "auto-set a place's photo from a paid provider" call site
  // (PlaceMenu.js, WalkAI.js, AISearch.js, TripAI.js, KMLInput.js,
  // ImageDialog.js's "Business Photos" tab) instead of each one inlining its
  // own provider ternary. A provider with no photo adapter (e.g. "google" —
  // there's no cheap Places Photo Media endpoint wired up here, see
  // parseNewAPIPlace's cost-control comment) is skipped, not treated as a
  // failure; the chain calls back with `null` if every entry is skipped or
  // genuinely finds nothing. Static since it needs no instance state.
  // Search term for places similar to one that's closed: a meal event
  // looks for somewhere to eat regardless of the closed place's own
  // category; otherwise the category name itself ("Art Museum", "Park")
  // works as a plain Google Maps query.
  static alternativeSearchQuery = (eventSubject, categoryName) => {
    const s = (eventSubject || "").toLowerCase();
    if (/\bbreakfast\b/.test(s)) return "breakfast";
    if (/\b(lunch|dinner|brunch)\b/.test(s)) return "restaurant";
    if (categoryName && categoryName !== "NA") return categoryName;
    return "restaurant";
  };

  // "Find a new place" for a closed event: places near `closedPlace` that
  // are definitely open for the whole of [start, end] (unknown hours are
  // excluded), nearest first. Tries OpenWeb Ninja first — it's a plain
  // Google Maps search that returns opening hours at no extra cost — and
  // falls back to Google (with hours requested) only if it errors or
  // nothing qualifies. Results aren't in the guide yet; City/Country are
  // copied from the closed place since the search parsers don't set them.
  static MAX_ALTERNATIVE_DISTANCE_KM = 2;
  static findOpenAlternatives = (closedPlace, eventSubject, start, end, categoryName, callback, limit = 5) => {
    if (!closedPlace || !closedPlace.Lat || !closedPlace.Longi) {
      callback([]);
      return;
    }
    const query = GooglePlaces.alternativeSearchQuery(eventSubject, categoryName);
    const gp = new GooglePlaces();
    const lat = closedPlace.Lat;
    const lng = closedPlace.Longi;
    const qualify = (places) =>
      (places || [])
        .filter((p) => p && p.Name && p.Name !== closedPlace.Name)
        .map((p) => ({
          p,
          km: Common.calculateDistance({ latitude: lat, longitude: lng }, { latitude: p.Lat, longitude: p.Longi }),
        }))
        .filter(({ p, km }) => {
          if (km > GooglePlaces.MAX_ALTERNATIVE_DISTANCE_KM) return false;
          const conflict = Place.getOpeningHoursConflict({ Places: "candidate", Start: start, End: end }, { getPlace: () => p });
          return conflict.status === "open";
        })
        .sort((a, b) => a.km - b.km)
        .slice(0, limit)
        .map(({ p }) => {
          p.City = closedPlace.City;
          p.Country = closedPlace.Country;
          return p;
        });
    const tryGoogle = () =>
      gp._searchPlacesGoogle(query, null, lat, lng, 2000, (places) => callback(qualify(places)), true, AICategories, AIBaseCategories);
    gp._searchPlacesOpenWebNinja(
      query,
      lat,
      lng,
      (places) => {
        const found = qualify(places);
        if (found.length > 0) callback(found);
        else tryGoogle();
      },
      tryGoogle,
      AICategories,
      AIBaseCategories
    );
  };

  static getPhotoProviderChain = () => ({
    searchPlacePhoto: (query, callback, lat, lng) => {
      const PHOTO_PROVIDERS = { hasdata: HasDataPlaces, openwebninja: OpenWebNinjaPlaces };
      const runProvider = (index) => {
        if (index >= PLACES_PROVIDER_ORDER.length) { callback(null); return; }
        const adapter = PHOTO_PROVIDERS[PLACES_PROVIDER_ORDER[index]];
        if (!adapter) { runProvider(index + 1); return; }
        adapter.searchPlacePhoto(query, (photo) => {
          if (photo) { callback(photo); return; }
          runProvider(index + 1);
        }, lat, lng);
      };
      runProvider(0);
    },
  });

  _searchPlacesGoogle = (textQuery, includedType, lat, lng, radiusMeters, callback, wantHours, aiCategories = [], aiBaseCategories = []) => {
    const url = `${API_BASE_URL}/google_places_new_search.php`;
    const body = JSON.stringify({ textQuery, includedType, lat, lng, radiusMeters, wantHours });
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        clearTimeout(timeoutId);
        console.log("searchPlacesNewAPI response", JSON.stringify(data));
        // Our own daily limit isn't an error to shout about — note it for the
        // screen to report once (see Common/GateLimit.js).
        if (isDailyLimit(data.error)) noteDailyLimit();
        else if (data.error) console.error("searchPlacesNewAPI API error", data.error);
        const places = (data.places || []).map((p, i) =>
          this.parseNewAPIPlace(p, i, aiCategories, aiBaseCategories)
        );
        callback(places);
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        console.error("searchPlacesNewAPI fetch error", err);
        callback([]);
      });
  };

  // hasdata_places_search.php already normalizes HasData's two possible
  // response shapes (placeResults/localResults) into a flat
  // {places:[{name,lat,lng,...}]} array server-side — see that file's own
  // comment for which fields are confirmed-live vs. best-effort. This just
  // maps that flat shape onto a Place, same as parseNewAPIPlace does for
  // Google's shape. `onFailure` (added 2026-09-24) is called instead of
  // `callback([])` on a genuine upstream error — see searchPlacesNewAPI's
  // own comment for why a real "no results" response does NOT trigger it.
  _searchPlacesHasData = (textQuery, lat, lng, callback, onFailure, aiCategories = [], aiBaseCategories = []) => {
    const url = `${API_BASE_URL}/hasdata_places_search.php`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ textQuery, lat, lng }),
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        clearTimeout(timeoutId);
        console.log("_searchPlacesHasData response", JSON.stringify(data));
        if (isDailyLimit(data.error)) {
          // Our own allowance is used up — stop here; don't push the same
          // request onto the next (costlier) provider. See GateLimit.js.
          noteDailyLimit();
          callback([]);
          return;
        }
        if (data.error) {
          console.error("_searchPlacesHasData API error — falling back to Google", data.error);
          if (onFailure) { onFailure(); return; }
          callback([]);
          return;
        }
        callback((data.places || []).map((p) => this.parseHasDataPlace(p, aiCategories, aiBaseCategories)));
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        console.error("_searchPlacesHasData fetch error — falling back to Google", err);
        if (onFailure) onFailure(); else callback([]);
      });
  };

  // HasData/OpenWeb Ninja return human-readable Title Case type strings
  // ("Historical landmark", "Italian restaurant") — Common.js's
  // PLACE_TYPE_DURATIONS keys are Google-style lowercase snake_case
  // ("historical_landmark", "restaurant"), so a raw match against
  // estimateDurationForTypes silently misses on every scraper-sourced
  // place, always landing on the generic 60-min fallback instead of a real
  // type-based estimate. Confirmed live 2026-09-24 (Common.estimateDuration
  // ForTypes does an exact hasOwnProperty check, no normalization of its
  // own). This only feeds the duration lookup — place.Category keeps the
  // original raw strings, same as parseNewAPIPlace's Google-types
  // precedent, since Category's real consumers are separate from this.
  _normalizeTypeKeysForDuration = (rawList) => {
    if (!Array.isArray(rawList)) return [];
    const normalized = [];
    rawList.forEach((raw) => {
      const key = String(raw).toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
      if (!key) return;
      normalized.push(key);
      // Cuisine-prefixed restaurant subtypes ("italian_restaurant",
      // "tuscan_restaurant", "mediterranean_restaurant" — confirmed live
      // 2026-09-24 against real OpenWeb Ninja results) don't match the
      // plain "restaurant" key on their own; add it explicitly so these
      // still get a real duration estimate.
      if (key.endsWith("_restaurant") && key !== "restaurant") normalized.push("restaurant");
    });
    return normalized;
  };

  parseHasDataPlace = (p, aiCategories = [], aiBaseCategories = []) => {
    const place = new Place();
    place.PType = "Icon";
    place.Source = Place_Source.GOOGLEPLACES;

    place.Name = p.name || "Unknown";
    place.Lat = typeof p.lat === "number" ? p.lat : 0;
    place.Longi = typeof p.lng === "number" ? p.lng : 0;
    place.Address = p.address || "";
    if (p.rating) place.Rating = parseFloat(p.rating) || undefined;

    const mapsUrl = p.maps_link || `https://www.google.com/maps/search/?api=1&query=${place.Lat},${place.Longi}`;
    place.Hlink = `Google Maps|${mapsUrl}`;
    if (p.website) place.Hlink += `;Website|${p.website}`;
    place.Link = { type: "url", value: mapsUrl, offset: "0" };

    if (p.photo) Object.assign(place, googlePhotoFields(p.photo)); // thumbnail + 1600 px main (2026-11-04)

    // Merged, not cascaded — see parseOpenWebNinjaPlace's identical fix
    // comment for why an empty (but present) `types` array must not
    // silently discard a real `category` value.
    const typeList = [
      ...(Array.isArray(p.types) ? p.types : []),
      ...(p.category ? [p.category] : []),
    ];
    // Resolve a real guide category ID when real categories were passed in
    // (added 2026-09-24, same pattern as parseNewAPIPlace/parsePlace) —
    // falls back to the raw type list when they weren't, so any existing
    // caller that does its own re-mapping afterward is unaffected.
    const hasCategories = aiCategories.length > 0 || aiBaseCategories.length > 0;
    place.Category = hasCategories
      ? [mapGoogleTypesToCategoryId(typeList, aiCategories, aiBaseCategories)].filter(Boolean)
      : typeList;
    place.Group = [];
    place.Duration = Common.estimateDurationForTypes(this._normalizeTypeKeysForDuration(typeList), place.Name);

    let info = "";
    if (p.open_now !== null && p.open_now !== undefined) {
      const isOpen = /open/i.test(String(p.open_now)) && !/closed/i.test(String(p.open_now));
      info += `<p>${isOpen ? "Open now" : "Closed now"}</p>`;
    }
    place.Info = info;

    // hours_raw's real shape isn't confirmed yet (see
    // hasdata_places_search.php's comment) — best-effort parse via the
    // shared day-map parser below; silently left at the default (open/
    // unknown) if the shape doesn't match what's handled there.
    const parsedHours = GooglePlaces.parseOpeningHoursFromDayMap(p.hours_raw);
    if (parsedHours) place.OpeningHours = parsedHours;

    return place;
  };

  // openwebninja_places_search.php is a raw passthrough (see that file's
  // comment for why) — this parser is the one place that needs updating
  // once the real maps/search response shape is confirmed. Checks several
  // plausible field-name candidates per value (mirroring the defensive
  // style hasdata_places_photo.php already uses for HasData's own
  // inconsistent casing) rather than assuming one exact set of names.
  _firstDefined = (obj, keys) => {
    for (const k of keys) {
      if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== "") return obj[k];
    }
    return undefined;
  };

  // `onFailure` (added 2026-09-24) is called instead of `callback([])` on a
  // genuine upstream error (network failure, or an explicit `error` field —
  // see openwebninja_places_search.php's own comment for why httpCode>=400/
  // a "status": "ERROR" envelope is now distinguished server-side from a
  // legitimate zero-result search) — searchPlacesNewAPI wires this to retry
  // the same query against Google rather than surfacing an empty result.
  _searchPlacesOpenWebNinja = (textQuery, lat, lng, callback, onFailure, aiCategories = [], aiBaseCategories = []) => {
    const url = `${API_BASE_URL}/openwebninja_places_search.php`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ textQuery, lat, lng }),
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        clearTimeout(timeoutId);
        // Left at console.log (not removed after this feature settles) on
        // purpose — this is the fastest way to confirm/correct
        // parseOpenWebNinjaPlace's field-name guesses against a real
        // response the first time this provider is actually used live.
        console.log("_searchPlacesOpenWebNinja response", JSON.stringify(data));
        if (isDailyLimit(data.error)) {
          // Our own allowance is used up — stop here; don't push the same
          // request onto the next (costlier) provider. See GateLimit.js.
          noteDailyLimit();
          callback([]);
          return;
        }
        if (data.error) {
          console.error("_searchPlacesOpenWebNinja API error — falling back to Google", data.error);
          if (onFailure) { onFailure(); return; }
          callback([]);
          return;
        }
        callback((data.places || []).map((p) => this.parseOpenWebNinjaPlace(p, aiCategories, aiBaseCategories)));
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        console.error("_searchPlacesOpenWebNinja fetch error — falling back to Google", err);
        if (onFailure) onFailure(); else callback([]);
      });
  };

  // Field names/shapes confirmed live 2026-09-24 from a real sample
  // response (a "Hotels in San Francisco, USA" query against this same
  // endpoint) — see openwebninja_places_search.php's file comment for the
  // full confirmed field list. latitude/longitude are top-level (not
  // nested under a gps_coordinates object like HasData's), address is
  // already a plain string, type is singular (not "category"), and the
  // maps link field is place_link.
  parseOpenWebNinjaPlace = (p, aiCategories = [], aiBaseCategories = []) => {
    const place = new Place();
    place.PType = "Icon";
    place.Source = Place_Source.GOOGLEPLACES;
    const f = this._firstDefined;

    place.Name = f(p, ["name", "title"]) || "Unknown";
    place.Lat = typeof p.latitude === "number" ? p.latitude : 0;
    place.Longi = typeof p.longitude === "number" ? p.longitude : 0;
    place.Address = f(p, ["address", "full_address", "formatted_address"]) || "";
    const rating = f(p, ["rating"]);
    if (rating) place.Rating = parseFloat(rating) || undefined;

    const placeId = f(p, ["place_id", "google_place_id"]);
    const mapsUrl = f(p, ["place_link", "google_maps_link", "link", "maps_link"])
      || (placeId ? `https://www.google.com/maps/place/?q=place_id:${placeId}` : `https://www.google.com/maps/search/?api=1&query=${place.Lat},${place.Longi}`);
    place.Hlink = `Google Maps|${mapsUrl}`;
    const website = f(p, ["website", "website_url"]);
    if (website) place.Hlink += `;Website|${website}`;
    place.Link = { type: "url", value: mapsUrl, offset: "0" };

    // Confirmed live 2026-09-23/24 — photos_sample[].photo_url is a direct
    // lh3.googleusercontent.com link, same free-quality tier as HasData's.
    const photoUrl = Array.isArray(p.photos_sample) && p.photos_sample.length > 0
      ? f(p.photos_sample[0], ["photo_url", "url"])
      : f(p, ["photo_url", "thumbnail"]);
    if (photoUrl) Object.assign(place, googlePhotoFields(photoUrl)); // thumbnail + 1600 px main (2026-11-04)

    // Merged rather than cascaded (subtypes, ELSE types, ELSE type) — an
    // earlier version returned as soon as `subtypes` was an array at all,
    // even an EMPTY one, silently discarding a real singular `type` value
    // in that case and leaving the place with zero category-matching
    // signal for BuildMyTripWizard.js's mapGoogleTypesToCategoryId. Found
    // 2026-09-24 investigating real "category not found" reports.
    const typeList = [
      ...(Array.isArray(p.subtypes) ? p.subtypes : []),
      ...(Array.isArray(p.types) ? p.types : []),
      ...(f(p, ["type", "category"]) ? [f(p, ["type", "category"])] : []),
    ];
    // Resolve a real guide category ID when real categories were passed in
    // (added 2026-09-24, same pattern as parseNewAPIPlace/parsePlace) —
    // falls back to the raw type list when they weren't, so any existing
    // caller that does its own re-mapping afterward (e.g.
    // BuildMyTripWizard.js) is unaffected.
    const hasCategories = aiCategories.length > 0 || aiBaseCategories.length > 0;
    place.Category = hasCategories
      ? [mapGoogleTypesToCategoryId(typeList, aiCategories, aiBaseCategories)].filter(Boolean)
      : typeList;
    place.Group = [];
    place.Duration = Common.estimateDurationForTypes(this._normalizeTypeKeysForDuration(typeList), place.Name);

    let info = "";
    const openingStatus = f(p, ["opening_status", "open_now"]);
    if (openingStatus !== undefined && openingStatus !== null) {
      const isOpen = /open/i.test(String(openingStatus)) && !/closed/i.test(String(openingStatus));
      info += `<p>${isOpen ? "Open now" : "Closed now"}</p>`;
    }
    place.Info = info;

    // working_hours confirmed live 2026-09-24: an object keyed by full day
    // name, each value an ARRAY of strings (e.g. {"Monday": ["Open 24
    // hours"]} or {"Monday": ["9:00 AM - 5:00 PM"]}), empty {} when no
    // hours data exists for the place — handled by the shared parser below.
    const parsedHours = GooglePlaces.parseOpeningHoursFromDayMap(p.working_hours);
    if (parsedHours) place.OpeningHours = parsedHours;

    return place;
  };

  // Shared parser for the "day name -> hours" shape several scraper APIs
  // use for opening hours. Confirmed live 2026-09-24 for OpenWeb Ninja's
  // working_hours field: an object keyed by full day name, each value an
  // ARRAY of strings — e.g. { Monday: ["Open 24 hours"] } or
  // { Monday: ["9:00 AM - 5:00 PM"] }, empty {} when no hours data exists.
  // Also handles a plain string per day (day: "9:00 AM - 5:00 PM") and an
  // array of "Monday: 9:00 AM - 5:00 PM" lines, in case HasData's hours_raw
  // (still unconfirmed — see hasdata_places_search.php) turns out to use
  // either of those instead. Returns null (leaving OpeningHours at its
  // "unknown" default) for anything else rather than guessing wrong.
  static DAY_NAME_TO_APP_DAY = {
    sun: "Sun", sunday: "Sun", mon: "Mon", monday: "Mon", tue: "Tue", tues: "Tue", tuesday: "Tue",
    wed: "Wed", wednesday: "Wed", thu: "Thu", thur: "Thu", thurs: "Thu", thursday: "Thu",
    fri: "Fri", friday: "Fri", sat: "Sat", saturday: "Sat",
  };
  static parseOpeningHoursFromDayMap(raw) {
    if (!raw) return null;
    let entries = [];
    if (Array.isArray(raw)) {
      // ["Monday: 9:00 AM - 5:00 PM", ...]
      entries = raw.map((line) => {
        const m = /^([A-Za-z]+)\s*:\s*(.+)$/.exec(String(line));
        return m ? [m[1], m[2]] : null;
      }).filter(Boolean);
    } else if (typeof raw === "object") {
      entries = Object.entries(raw);
    } else {
      return null;
    }
    if (entries.length === 0) return null;

    const timeRe = /(\d{1,2}(?::\d{2})?)\s*([AaPp][Mm])?\s*[-–—]\s*(\d{1,2}(?::\d{2})?)\s*([AaPp][Mm])/;
    const to24h = (t) => {
      const m = /^(\d{1,2})(?::(\d{2}))?\s*([AaPp][Mm])$/.exec(t.trim());
      if (!m) return null;
      let h = parseInt(m[1], 10);
      const min = m[2] ? parseInt(m[2], 10) : 0;
      const isPM = /p/i.test(m[3]);
      if (isPM && h !== 12) h += 12;
      if (!isPM && h === 12) h = 0;
      return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
    };

    const hours = Place.getDefaultOpeningHours();
    let matched = 0;
    entries.forEach(([dayRaw, rawValue]) => {
      const day = GooglePlaces.DAY_NAME_TO_APP_DAY[String(dayRaw).trim().toLowerCase()];
      if (!day) return;
      // OpenWeb Ninja's confirmed shape is an array of strings per day — a
      // plain string is also accepted in case another vendor uses that.
      // Every range in every string is kept: this used to read only the
      // first string's first range, so a restaurant open "12–3 PM, 7–11 PM"
      // kept only its lunch window and looked closed at dinner.
      const texts = (Array.isArray(rawValue) ? rawValue : [rawValue]).map((t) => String(t || "")).filter(Boolean);
      if (texts.length === 0) return;
      const text = texts.join(", ");
      if (/closed/i.test(text)) {
        hours[day] = { periods: [], closed: true };
        matched++;
        return;
      }
      if (/24\s*hours|open\s*24/i.test(text)) {
        hours[day] = { periods: [{ open: "00:00", close: "23:59" }], closed: false };
        matched++;
        return;
      }
      const periods = [];
      const re = new RegExp(timeRe.source, "g");
      let m;
      while ((m = re.exec(text)) !== null) {
        const close = to24h(`${m[3]} ${m[4]}`);
        // Google-style "12–3 PM" drops the opening side's AM/PM — inherit
        // the closing side's, unless that makes it later than close
        // ("11–3 PM" is 11 AM, not 11 PM).
        let open = to24h(`${m[1]} ${m[2] || m[4]}`);
        if (!m[2] && open && close && open > close) open = to24h(`${m[1]} ${/p/i.test(m[4]) ? "AM" : "PM"}`);
        if (open && close) periods.push({ open, close });
      }
      if (periods.length > 0) {
        hours[day] = { periods, closed: false };
        matched++;
      }
    });
    return matched > 0 ? hours : null;
  }

  parseNewAPIPlace = (p, index, aiCategories = [], aiBaseCategories = []) => {
    // No manual place.ID assignment here — new Place() (with no args) already
    // auto-generates a genuinely unique random ID checked against
    // window.guide_obj.places when window.web_app is true (see Place.js's
    // constructor), the same thing the legacy parsePlace() relies on.
    // A previous version of this line overwrote that with `gnew_${Date.now()}_
    // ${index}` — harmless for a single search batch, but searchCityAttractions
    // (three back-to-back searchPlacesNewAPI calls) can have two batches parse
    // in the same millisecond, each restarting `index` at 0, producing
    // duplicate IDs across batches that collapse in React's keyed rendering —
    // exactly why 17 "search results" were counted but nothing displayed.
    const place = new Place();
    place.PType = "Icon";
    place.Source = Place_Source.GOOGLEPLACES;

    place.Name = p.displayName?.text || "Unknown";
    place.Lat = p.location?.latitude ?? 0;
    place.Longi = p.location?.longitude ?? 0;
    place.Address = p.formattedAddress || "";
    if (p.rating) place.Rating = p.rating;

    const mapsUrl = p.googleMapsUri
      || (p.id ? `https://www.google.com/maps/place/?q=place_id:${p.id}` : `https://www.google.com/maps/search/?api=1&query=${place.Lat},${place.Longi}`);
    place.Hlink = `Google Maps|${mapsUrl}`;
    if (p.websiteUri) place.Hlink += `;Website|${p.websiteUri}`;
    place.Link = { type: "url", value: mapsUrl, offset: "0" };

    // No photo here — places.photos is deliberately not in the field mask
    // (see google_places_new_search.php's comment: a live Places Photo Media
    // URL gets re-billed by Google on every future view, by every user,
    // forever, and was ~75% of a real $360 two-week testing bill). Left at
    // Place.js's constructor default ("") and filled in afterward via the
    // cheaper Custom Search → Wikimedia → Flickr chain — see
    // BuildMyTripWizard.js's enrichPlacePhoto (PlaceMenu.autoSetPhoto).
    // Resolve a real guide category ID when real categories were passed in
    // (added 2026-09-24, mirroring parsePlace's — the legacy search path's
    // — existing behavior) — falls back to Google's raw, unmapped type
    // strings when they weren't, so any existing caller that does its own
    // re-mapping afterward (e.g. BuildMyTripWizard.js's own
    // mapGoogleTypesToCategoryId pass) is unaffected. Without this,
    // AISearch.js's/TripAI.js's `alreadyCategorized` check (added earlier
    // today) would misread a raw, unmapped type string as "already has a
    // real category" and skip its own fallback matching — the exact
    // regression flagged when scoping this fix.
    const hasCategories = aiCategories.length > 0 || aiBaseCategories.length > 0;
    place.Category = hasCategories
      ? [mapGoogleTypesToCategoryId(p.types, aiCategories, aiBaseCategories)].filter(Boolean)
      : (p.types || []);
    place.Group = [];
    // Places API has no duration field of its own — estimate from the
    // place's own type(s) instead of leaving every result at the generic
    // 30-minute default (Place.js's constructor value), so schedules built
    // from these results have a more realistic per-stop time budget.
    place.Duration = Common.estimateDurationForTypes(p.types, place.Name);

    // editorialSummary/generativeSummary/reviewSummary are deliberately not
    // requested (see google_places_new_search.php's field mask — they push
    // every call to Google's priciest billing tier) — place.Info's
    // descriptive text comes from free Wikipedia enrichment instead (e.g.
    // BuildMyTripWizard.js's enrichPlaceInfo).
    let info = "";
    if (p.priceLevel) info += `<p><strong>Price:</strong> ${p.priceLevel.replace("PRICE_LEVEL_", "").toLowerCase()}</p>`;
    if (p.regularOpeningHours?.openNow !== undefined)
      info += `<p>${p.regularOpeningHours.openNow ? "Open now" : "Closed now"}</p>`;
    place.Info = info;

    if (p.regularOpeningHours?.periods) {
      const parsedHours = GooglePlaces.parseOpeningHoursFromNewAPI(p.regularOpeningHours.periods);
      if (parsedHours) place.OpeningHours = parsedHours;
    }

    return place;
  };

  // Sources candidate places for a city by running a few `includedType`
  // passes through searchPlacesNewAPI and merging/de-duping the results —
  // used by "Plan with Google Places" instead of an LLM-generated place
  // list. Drops places with impractically narrow opening hours (see
  // hasImpracticalHours below) before they ever reach a scheduler, since
  // real hours data is already on hand here — the LLM path never has that
  // luxury before scheduling. Sorted by rating (unrated last) and capped to
  // maxResults.
  // keepImpracticalHours (default false) — the automatic, no-review path
  // wants difficult-to-schedule places pre-stripped so it never wastes a
  // slot on one; a manual "let me pick" reviewer should see everything
  // found, including the ones with narrow hours, so they can judge and
  // decide for themselves rather than have candidates silently disappear
  // before the picking screen ever renders. Either way, the sparse-
  // destination fallback below still triggers off the PRACTICAL count —
  // whether the pool is thin is a real-candidates question independent of
  // what the caller ultimately wants shown.
  // Converts a FeaturedPlaceImages.json manifest entry into a real Place —
  // same shape parseNewAPIPlace produces, minus the fields a Featured
  // Guide's place data never carried (Address/Phone/live Rating). Photo/Photo_m/Info come pre-filled from the manifest
  // itself, so these never need the enrichPlacePhoto/PlaceMenu.autoSetPhoto
  // pass other Google-sourced places go through.
  parseFeaturedPlace = (entry, city, country) => {
    const place = new Place();
    place.PType = "Icon";
    place.Source = Place_Source.FEATURED;
    place.Name = entry.name || "Unknown";
    place.Lat = typeof entry.lat === "number" ? entry.lat : 0;
    place.Longi = typeof entry.longi === "number" ? entry.longi : 0;
    place.Photo = entry.photo || "";
    place.Photo_m = entry.photo_m || entry.photo || "";
    place.Info = entry.info || "";
    place.City = city;
    place.Country = country;
    // Opening hours from the Featured guide (compact form, see
    // Place.openingHoursFromCompact) and the date they were looked up — so a
    // Featured place needs no paid hours lookup, and one with none found
    // isn't looked up again (GooglePlaces.fetchOpeningHours). Added to the
    // manifest 2026-10-16; older entries have neither.
    if (entry.hours) place.OpeningHours = Place.openingHoursFromCompact(entry.hours);
    if (entry.hoursChecked) place.HoursChecked = entry.hoursChecked;
    // entry.category is a NAME (e.g. "Museum"), not an ID — resolved from
    // the source Featured Guide's own category list when the manifest was
    // built (see scripts/addCategoriesToManifest.js), since category IDs
    // aren't guaranteed consistent across guides beyond the shared baseline.
    // Re-resolved here against the CURRENT guide's own category list (every
    // guide is seeded with the full baseline set, so an exact name match is
    // expected for the vast majority of entries) rather than guessing an ID.
    if (entry.category && window.guide_obj?.categories) {
      const match = window.guide_obj.categories.find(
        (c) => (c.name || "").toLowerCase() === entry.category.toLowerCase()
      );
      if (match) place.Category = [match.id];
    }
    // entry.duration (minutes) is inferred from the REAL curated schedule
    // of the Featured Guide(s) this place came from — see
    // scripts/addDurationsToManifest.js — the median length of whatever
    // single-place schedule events covered a visit to it, which is a much
    // better signal than a category guess. Only ~99.99% of manifest
    // entries have one (a coordinate that never appeared in a usable
    // single-place event), so the category-based estimate stays as a
    // fallback: Featured places have no Google `types` array to estimate
    // from (they never went through parseNewAPIPlace/searchPlacesNewAPI at
    // all), so the resolved category name doubles as a rough type there
    // (converted to PLACE_TYPE_DURATIONS' snake_case key format), same as
    // the mega-attraction name check every other duration estimate gets.
    place.Duration = typeof entry.duration === "number"
      ? Common.applyMegaAttractionFloor(entry.duration, entry.name)
      : Common.estimateDurationForTypes(
          entry.category ? [entry.category.toLowerCase().replace(/\s+/g, "_")] : [],
          entry.name
        );
    return place;
  };

  // MIN_FEATURED_COVERAGE (15) — the bar above which a destination's
  // Featured-Guides manifest bucket is treated as "enough candidates,"
  // trading the live Places Text Search call (the one remaining billed cost
  // once photos moved off Places API) for zero-cost, pre-enriched manifest
  // places. 231 of the manifest's 444 cities clear this bar — see memory:
  // project_destinations_first_redesign_idea for the full coverage
  // breakdown this threshold was picked from. Below the bar, or for any
  // city with no manifest coverage at all, falls through to the normal live
  // search unchanged.
  MIN_FEATURED_COVERAGE = 15;

  searchCityAttractions = async (city, country, lat, lng, maxResults, callback, keepImpracticalHours = false) => {
    const featuredBucket = await getFeaturedCityBucket(country, city, this.MIN_FEATURED_COVERAGE);
    if (featuredBucket) {
      const places = featuredBucket
        .map((entry) => this.parseFeaturedPlace(entry, city, country))
        .filter((p) => !GooglePlaces.hasNonEnglishName(p));
      callback(places.slice(0, maxResults));
      return;
    }

    const radiusMeters = 15000;
    const collected = [];
    const seen = new Set(); // name+rounded-coords — good enough to dedupe against the fallback pass

    const addResults = (places) => {
      places.forEach((p) => {
        const key = `${p.Name.toLowerCase()}|${p.Lat.toFixed(3)}|${p.Longi.toFixed(3)}`;
        if (!seen.has(key)) {
          seen.add(key);
          // parseNewAPIPlace never sets City/Country (Google's searchText
          // response only carries a full formattedAddress string, not
          // structured locality/country fields, since those aren't in the
          // field mask) — every result from THIS call is already known to
          // belong to `city`/`country`, so just stamp it directly instead of
          // trying to parse it back out of an address string. Without this,
          // every Google-sourced place had no City at all, which showed up
          // as the Schedule day-header's per-day city label working for the
          // one imported/hand-entered place in a trip but silently blank
          // for every day built from a Google search.
          p.City = city;
          p.Country = country;
          collected.push(p);
        }
      });
    };

    const finish = () => {
      // Non-English names are excluded unconditionally (even for manual
      // review) — unlike narrow opening hours, an unreadable name isn't
      // something a reviewer would ever want to see and pick anyway.
      const readable = collected.filter((p) => !GooglePlaces.hasNonEnglishName(p));
      const filtered = keepImpracticalHours ? readable : readable.filter((p) => !GooglePlaces.hasImpracticalHours(p));
      filtered.sort((a, b) => (b.Rating || 0) - (a.Rating || 0));
      callback(filtered.slice(0, maxResults));
    };

    // Single unfiltered call instead of one pass per type
    // (tourist_attraction/museum/park) — Google's own relevance ranking for
    // "top attractions in X" already surfaces a natural mix of these without
    // needing to ask 3 times, cutting this from 3-4 Places API (New) calls
    // per destination down to 1 (occasionally 2, for the sparse-destination
    // fallback below). Trade-off: no longer an explicit guarantee that
    // museums/parks specifically are represented if "top attractions"
    // ranks them low for a given city — accepted since maxResultCount=20
    // already returns a healthy pool to sort/cap from.
    //
    // Sparse-destination fallback — a small town can still come back thin
    // from the single pass above (confirmed empirically against real
    // low-popularity destinations like Mekelle, Ethiopia). A broader pass
    // over a wider radius with a different query phrasing catches real
    // places the first pass missed — confirmed empirically to turn
    // Mekelle's 6 results into 20+ (parks, gardens, landmarks, plus some
    // noise like tour agencies, which the existing rating sort and
    // maxResults cap below mostly filter back out). Google's Places API
    // (New) caps a single searchText call at 20 results regardless of what
    // maxResultCount asks for (see google_places_new_search.php's hardcoded
    // value) — an extra query is the only way to get more, so this only
    // runs when the first pass actually came back low, not for every
    // well-covered city.
    const MIN_RESULTS_BEFORE_FALLBACK = 8;
    const FALLBACK_RADIUS_METERS = 30000;

    this.searchPlacesNewAPI(`top attractions in ${city}, ${country}`, null, lat, lng, radiusMeters, (places) => {
      addResults(places);
      const practicalCount = collected.filter((p) => !GooglePlaces.hasNonEnglishName(p) && !GooglePlaces.hasImpracticalHours(p)).length;
      if (practicalCount >= MIN_RESULTS_BEFORE_FALLBACK) {
        finish();
        return;
      }
      this.searchPlacesNewAPI(`things to do in ${city}, ${country}`, null, lat, lng, FALLBACK_RADIUS_METERS, (fallbackPlaces) => {
        addResults(fallbackPlaces);
        finish();
      });
    });
  };

  // A place counts as impractically narrow if it's open on 2 or fewer days
  // a week, or (on the days it IS open) never open for more than ~2 hours
  // straight — e.g. a market only open Saturday mornings. No opening-hours
  // data at all is NOT impractical — most places have none, and that's
  // treated as "unknown"/fine everywhere else in this app, not excluded.
  static hasImpracticalHours = (place) => {
    if (!place.OpeningHours) return false;
    const toMinutes = (hhmm) => {
      const [h, m] = hhmm.split(":").map(Number);
      return h * 60 + m;
    };
    let openDays = 0;
    let maxPeriodMinutes = 0;
    Place.OPENING_HOURS_DAYS.forEach((day) => {
      const hours = place.OpeningHours[day];
      if (!hours || hours.closed || !Array.isArray(hours.periods)) return;
      const validPeriods = hours.periods.filter((p) => p.open && p.close);
      if (validPeriods.length === 0) return;
      openDays++;
      validPeriods.forEach((p) => {
        let mins = toMinutes(p.close) - toMinutes(p.open);
        if (mins < 0) mins += 24 * 60; // spans midnight
        if (mins > maxPeriodMinutes) maxPeriodMinutes = mins;
      });
    });
    if (openDays === 0) return false; // nothing usable parsed — treat as unknown, not impractical
    return openDays <= 2 || maxPeriodMinutes < 120;
  };

  // A place counts as non-English if its Google-returned name isn't
  // representable in Latin script -- e.g. Amharic, Chinese, Thai, Arabic.
  // google_places_new_search.php already requests languageCode: "en" so
  // Google returns an English name where it has one, but for a place with
  // no English name at all, Google just returns the local-script name
  // regardless (confirmed empirically -- Mekelle's CheleAnqua Waterfall has
  // no English name and comes back in Ge'ez script even with languageCode
  // set) -- this is the backstop for those. \u0370-\u1FFF covers Greek
  // through Ethiopic/Cherokee/Indic/Southeast-Asian scripts; \u2C00-\uFFFF
  // covers Glagolitic onward (Hiragana/Katakana, Hangul, CJK, etc.) -- both
  // ranges deliberately skip \u2000-\u2BFF (general punctuation, currency,
  // arrows, math/misc symbols, dingbats) so an em dash or bullet in an
  // otherwise-English name doesn't trigger a false exclusion.
  static NON_LATIN_SCRIPT_RE = /[\u0370-\u1FFF\u2C00-\uFFFF]/;
  static hasNonEnglishName = (place) => GooglePlaces.NON_LATIN_SCRIPT_RE.test(place.Name || "");

  // Google's Places API (New) day index: 0=Sunday..6=Saturday.
  static GOOGLE_DAY_TO_APP_DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Maps regularOpeningHours.periods (New Places API shape:
  // { open: { day, hour, minute }, close: { day, hour, minute } }) into this
  // app's { Mon..Sun: { periods: [{open, close}, ...], closed } } format.
  // Every period is kept (a day can have multiple, e.g. separate lunch/dinner
  // service). A day with no matching period is marked closed — Google only
  // omits a day from `periods` when the place doesn't publish hours for it,
  // EXCEPT for a place open 24 hours a day, 7 days a week: Google signals
  // that with a single period, day 0, opening at 00:00, with no `close` at
  // all (not just a missing hour/minute — normal periods always have a
  // close). Without special-casing that, every day except day 0 would
  // never appear in `periods` and get marked closed by the fill-in loop
  // below, incorrectly flagging an always-open place as shut 6 days a week
  // (e.g. Loggia dei Lanzi, a permanently-open loggia).
  static parseOpeningHoursFromNewAPI(periods) {
    if (!Array.isArray(periods) || periods.length === 0) return null;
    if (
      periods.length === 1 &&
      periods[0].open?.day === 0 &&
      (periods[0].open?.hour ?? 0) === 0 &&
      (periods[0].open?.minute ?? 0) === 0 &&
      !periods[0].close
    ) {
      const hours = Place.getDefaultOpeningHours();
      Place.OPENING_HOURS_DAYS.forEach((day) => {
        hours[day] = { periods: [{ open: "00:00", close: "23:59" }], closed: false };
      });
      return hours;
    }
    const hours = Place.getDefaultOpeningHours();
    const daysSeen = new Set();
    periods.forEach((period) => {
      const open = period.open;
      if (!open || typeof open.day !== "number") return;
      const day = GooglePlaces.GOOGLE_DAY_TO_APP_DAY[open.day];
      if (!day) return;
      const close = period.close;
      const openStr = `${String(open.hour ?? 0).padStart(2, "0")}:${String(open.minute ?? 0).padStart(2, "0")}`;
      const closeStr = close && typeof close.hour === "number"
        ? `${String(close.hour).padStart(2, "0")}:${String(close.minute ?? 0).padStart(2, "0")}`
        : "23:59";
      hours[day].periods.push({ open: openStr, close: closeStr });
      daysSeen.add(day);
    });
    Place.OPENING_HOURS_DAYS.forEach((day) => {
      if (!daysSeen.has(day)) hours[day] = { periods: [], closed: true };
    });
    return hours;
  }

  static hasOpeningHoursData(hours) {
    if (!hours) return false;
    return Place.OPENING_HOURS_DAYS.some(
      (day) => hours[day] && (hours[day].closed === true || (hours[day].periods && hours[day].periods.length > 0))
    );
  }

  // Explicit "Find Opening Times" action: resolves a place by name+location
  // via Text Search (New API) rather than requiring an existing place_id,
  // since most places in this app don't have one. Calls back with a
  // structured OpeningHours object, or null if none could be found.
  //
  // HoursChecked (YYYY-MM-DD) remembers a lookup that found the place but no
  // hours (a square, a bridge, a viewpoint) — asking again within
  // HOURS_RECHECK_DAYS would only pay for the same empty answer, so it calls
  // back null at once. Set here, and on the Featured guides' places (and
  // FeaturedPlaceImages.json) by the 2026-10 opening-hours collection. Not
  // set when nothing was found at all: an empty result can also be today's
  // lookup limit or a network error, and those must be retried.
  static HOURS_RECHECK_DAYS = 180;
  static hoursCheckedRecently(place) {
    const t = place && place.HoursChecked ? Date.parse(place.HoursChecked) : NaN;
    return Number.isFinite(t) && Date.now() - t < GooglePlaces.HOURS_RECHECK_DAYS * 864e5;
  }
  static fetchOpeningHours(place, callback) {
    if (GooglePlaces.hoursCheckedRecently(place)) { callback(null); return; }
    const query = [place.Name, place.City, place.Country].filter(Boolean).join(", ");
    if (!query) { callback(null); return; }
    const gp = new GooglePlaces();
    gp.searchPlacesNewAPI(query, null, place.Lat, place.Longi, 200, (results) => {
      const found = results && results.length > 0;
      const hours = found ? results[0].OpeningHours : null;
      if (found) place.HoursChecked = new Date().toISOString().slice(0, 10);
      callback(GooglePlaces.hasOpeningHoursData(hours) ? hours : null);
    }, true);
  }

  // Legacy Places Details API shape: opening_hours.periods is
  // [{ open: { day, time: "0900" }, close: { day, time: "1700" } }] — time is
  // a zero-padded "HHmm" string, day is 0=Sunday..6=Saturday (same numbering
  // as the New API, different field shape). Every period is kept (a day can
  // have multiple, e.g. separate lunch/dinner service); unlisted day =
  // closed — EXCEPT a place open 24/7, which Google signals with a single
  // period, day 0, opening at "0000", with no `close` key at all. See
  // parseOpeningHoursFromNewAPI's comment for why that needs special-casing.
  static mapLegacyOpeningHours(opening_hours) {
    const periods = opening_hours?.periods;
    if (!Array.isArray(periods) || periods.length === 0) return null;
    if (periods.length === 1 && periods[0].open?.day === 0 && periods[0].open?.time === "0000" && !periods[0].close) {
      const hours = Place.getDefaultOpeningHours();
      Place.OPENING_HOURS_DAYS.forEach((day) => {
        hours[day] = { periods: [{ open: "00:00", close: "23:59" }], closed: false };
      });
      return hours;
    }
    const toHHMM = (time) =>
      typeof time === "string" && time.length === 4 ? `${time.slice(0, 2)}:${time.slice(2)}` : null;
    const hours = Place.getDefaultOpeningHours();
    const daysSeen = new Set();
    periods.forEach((period) => {
      const open = period.open;
      if (!open || typeof open.day !== "number") return;
      const day = GooglePlaces.GOOGLE_DAY_TO_APP_DAY[open.day];
      if (!day) return;
      const openStr = toHHMM(open.time);
      if (!openStr) return;
      const closeStr = (period.close && toHHMM(period.close.time)) || "23:59";
      hours[day].periods.push({ open: openStr, close: closeStr });
      daysSeen.add(day);
    });
    Place.OPENING_HOURS_DAYS.forEach((day) => {
      if (!daysSeen.has(day)) hours[day] = { periods: [], closed: true };
    });
    return hours;
  }
}
